import React from 'react';
import { ClockIcon, TruckIcon } from '@heroicons/react/24/outline';
import {
  SupplierProfile,
  SHIPPING_REGIONS,
  countryName,
  formatCatalogSize,
  formatShippingCost,
  formatShippingWindow,
  formatSyncInterval,
  formatVat,
  hasSupplierTerms,
  shippingRows,
} from '../lib/supplierProfile';

interface Props {
  profile: SupplierProfile | null | undefined;
  /** `compact` is the two-line summary on a supplier card; `full` is the connect dialog. */
  variant?: 'compact' | 'full';
}

const Term: React.FC<{ label: string; value: string | null }> = ({ label, value }) => (
  <div>
    <dt className="text-white/40 text-xs">{label}</dt>
    <dd className={`text-sm ${value ? 'text-white' : 'text-white/25'}`}>{value ?? 'Not stated'}</dd>
  </div>
);

/**
 * Read-only supplier terms for merchants. Shown before connecting, so a merchant learns
 * that a supplier ships in 4-5+ days from the card rather than from their first late order.
 *
 * "Not stated" is shown rather than hidden: a supplier that has not said how long shipping
 * takes is itself something a merchant should weigh.
 */
const SupplierProfileCard: React.FC<Props> = ({ profile, variant = 'full' }) => {
  const rows = shippingRows(profile?.shipping_times);
  const sync = formatSyncInterval(profile?.sync_interval_minutes);
  const stated = hasSupplierTerms(profile);

  if (variant === 'compact') {
    if (!stated && !sync) {
      return <p className="text-xs text-white/25">Shipping terms not stated yet</p>;
    }
    const cost = formatShippingCost(profile);
    return (
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-white/50">
        {rows.length > 0 ? (
          <span className="inline-flex items-center gap-1">
            <TruckIcon className="w-3.5 h-3.5 text-white/30" />
            {rows.map((r) => `${r.label} ${formatShippingWindow(r.window)}`).join(' · ')}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-white/25">
            <TruckIcon className="w-3.5 h-3.5" /> Shipping time not stated
          </span>
        )}
        {cost && <span>Shipping {cost}</span>}
        {sync && (
          <span className="inline-flex items-center gap-1">
            <ClockIcon className="w-3.5 h-3.5 text-white/30" /> Stock {sync.toLowerCase()}
          </span>
        )}
      </div>
    );
  }

  // Known regions are always listed so "does not ship" is visible, not implied by absence.
  const known = SHIPPING_REGIONS.map((r) => r.key);
  const extraRegions = rows.filter((r) => !known.includes(r.key));

  return (
    <div className="rounded-xl border border-white/[0.08] bg-white/[0.03] p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-white">Supplier terms</h4>
        <span className="text-[11px] text-white/30">Stated by the supplier</span>
      </div>

      {!stated && (
        <p className="text-xs text-yellow-400/80 mb-3">
          This supplier has not filled in its terms yet. Ask before relying on a shipping time.
        </p>
      )}

      <div className="mb-4">
        <p className="text-white/40 text-xs mb-1">Shipping time</p>
        <table className="w-full text-sm">
          <tbody>
            {SHIPPING_REGIONS.map(({ key, label }) => {
              const row = rows.find((r) => r.key === key);
              return (
                <tr key={key} className="border-t border-white/[0.06] first:border-t-0">
                  <td className="py-1.5 text-white/60">{label}</td>
                  <td className={`py-1.5 text-right ${row ? 'text-white' : 'text-white/25'}`}>
                    {stated || row ? formatShippingWindow(row?.window) : 'Not stated'}
                  </td>
                </tr>
              );
            })}
            {extraRegions.map((r) => (
              <tr key={r.key} className="border-t border-white/[0.06]">
                <td className="py-1.5 text-white/60">{r.label}</td>
                <td className="py-1.5 text-right text-white">{formatShippingWindow(r.window)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
        <Term label="Ships from" value={countryName(profile?.ships_from_country)} />
        <Term label="Shipping cost" value={formatShippingCost(profile)} />
        <Term label="VAT" value={formatVat(profile?.vat_treatment)} />
        <Term label="Stock refreshed" value={sync} />
        <Term label="Catalogue size" value={formatCatalogSize(profile?.max_catalog_size)} />
      </dl>

      {profile?.notes?.trim() && (
        <p className="mt-3 pt-3 border-t border-white/[0.06] text-sm text-white/70 whitespace-pre-line">
          {profile.notes.trim()}
        </p>
      )}
    </div>
  );
};

export default SupplierProfileCard;
